import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Api } from 'bimplus-websdk';
import { forkJoin, from, Observable, of } from 'rxjs';
import { catchError, concatMap, finalize, map, tap, toArray } from 'rxjs/operators';
import { BIMPLUS_API, BIMPLUS_API_CONFIG } from '@web-portal/domains/shared/bimplus-core';
import { RecentProjectsListServiceStrategy } from '../../strategies/recent-projects/recent-projects.strategy';
import { ProjectMapperStrategy, ProjectsServiceStrategy } from '../../strategies';
import { EnrichedRecentProject } from './recent-project.interface';
import { ROUTE } from '../../constants/route';
import { ProjectsService } from '../project/projects.service';

interface RecentProjectFetchResult {
  projectId: string;
  unavailableInPortal: boolean;
}

@Injectable()
export class RecentProjectsListService extends RecentProjectsListServiceStrategy<EnrichedRecentProject> {
  private readonly projectsAdapter = inject(ProjectsServiceStrategy);
  private readonly api: Api = inject(BIMPLUS_API);
  private readonly apiConfig = inject(BIMPLUS_API_CONFIG);
  private readonly router = inject(Router);
  private readonly projectMapper = inject(ProjectMapperStrategy);

  private isUnavailableInPortal(error: unknown): boolean {
    if (!error || typeof error !== 'object' || !('status' in error)) {
      return false;
    }

    const status = error.status;
    return status === 403 || status === 404;
  }

  /**
   * Loads enriched recent projects, fetching data from API for uncached projects.
   * Returns an Observable that completes when loading is done.
   */
  loadEnrichedProjects(): Observable<EnrichedRecentProject[]> {
    const storedProjects = this.recentProjectsService.loadRecentProjects();

    if (storedProjects.length === 0) {
      this.store.setRecentProjects([]);
      this.store.setLoading(false);
      return of([]);
    }

    // Get project IDs that need API calls (not in cache)
    const uncachedIds = this.recentProjectsService.getUncachedProjectIds(
      storedProjects.map(p => p.projectId)
    );

    // If all projects are cached, use cached data immediately
    if (uncachedIds.length === 0) {
      const enrichedProjects = this.recentProjectsService.loadEnrichedRecentProjects();
      this.store.setRecentProjects(enrichedProjects);
      this.store.setLoading(false);
      return of(enrichedProjects);
    }

    // Only fetch data for uncached projects
    const uncachedProjects = storedProjects.filter(p => uncachedIds.includes(p.projectId));

    // Process uncached projects sequentially to properly set team slug before each API call
    return from(uncachedProjects).pipe(
      concatMap(stored => {
        // Set team slug before making the API call
        this.api.setTeamSlug(stored.teamSlug);

        return forkJoin({
          project: this.projectsAdapter.getProjectById(stored.projectId),
          shortInfo: this.projectsAdapter
            .getTasksShortInfo(stored.projectId)
            .pipe(catchError(() => of([]))),
        }).pipe(
          map(({ project: apiProject, shortInfo }): RecentProjectFetchResult => {
            const project = this.projectMapper.mapToProject(apiProject, this.apiConfig);
            const tasksCount = ProjectsService.extractTotalTaskCount(shortInfo);
            this.recentProjectsService.setCachedEnrichedData(stored.projectId, {
              thumbnailUrl: project.thumbnailUrl,
              modelsCount: project.modelsCount,
              tasksCount,
            });

            return {
              projectId: stored.projectId,
              unavailableInPortal: false,
            };
          }),
          catchError(error =>
            of({
              projectId: stored.projectId,
              unavailableInPortal: this.isUnavailableInPortal(error),
            })
          )
        );
      }),
      toArray(),
      map(results => {
        const unavailableProjectIds = new Set(
          results.filter(result => result.unavailableInPortal).map(result => result.projectId)
        );

        return this.recentProjectsService
          .loadEnrichedRecentProjects()
          .filter(project => !unavailableProjectIds.has(project.projectId));
      }),
      tap(enrichedProjects => this.store.setRecentProjects(enrichedProjects)),
      finalize(() => this.store.setLoading(false)),
      catchError(() => {
        // Fallback to whatever is cached
        const enrichedProjects = this.recentProjectsService.loadEnrichedRecentProjects();
        this.store.setRecentProjects(enrichedProjects);
        return of(enrichedProjects);
      })
    );
  }

  /**
   * Selects a project, updates recent projects storage, and navigates to it.
   */
  selectProject(project: EnrichedRecentProject): void {
    // Update position in storage and cache (moves to most recent)
    this.recentProjectsService.addRecentProject(
      project.projectId,
      project.projectName,
      project.teamId,
      project.teamName,
      project.teamSlug,
      {
        thumbnailUrl: project.thumbnailUrl,
        modelsCount: project.modelsCount,
        tasksCount: project.tasksCount,
      }
    );

    this.router.navigate(['/', ROUTE.teams, project.teamId, ROUTE.projects, project.projectId]);
  }
}
